import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Anchor, Button, Checkbox, PasswordInput, Stack, TextInput } from '@mantine/core';
import { IconLogin } from '@tabler/icons-react';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { PlanningCentreButton } from '@/components/auth/PlanningCentreButton';
import { Callout, PageIntro } from '@/components/ui';
import { safeRedirect } from '@/lib/planningCentre';

/** Turn a sign-in failure into a sentence a volunteer can act on. */
function signInMessage(raw: string): string {
  const m = raw.toLowerCase();
  if (/email_not_confirmed/.test(m)) return 'Please confirm your email address first. Check your inbox for the link we sent.';
  if (/invalid|incorrect|wrong|credential|password|unauthori[sz]ed|401/.test(m)) {
    return "That email and password don't match. Please check them and try again.";
  }
  if (/locked|too many/.test(m)) return 'Too many tries. Please wait a few minutes, then try again.';
  if (/fetch|network|econn|timeout/.test(m)) return "We couldn't reach the server. Check your signal and try again.";
  return "We couldn't sign you in. Please try again.";
}

/** Sign in. Honours ?redirect= so a scanned procedure (/p/{code}) survives the round trip. */
export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useEnrichedAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirectTo = safeRedirect(searchParams.get('redirect'));

  if (auth.isAuthenticated && !submitting) return <Navigate to={redirectTo} replace />;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await auth.login(email.trim(), password, rememberMe);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(signInMessage(String((err as { message?: string })?.message || '')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="proc-main">
      <PageIntro title="Sign in" lede="Sign in to run procedures that are only for your team, and to see your runs." />
      <div className="proc-card" style={{ padding: 18 }}>
        <PlanningCentreButton redirectTo={redirectTo} withDivider />
        <form onSubmit={onSubmit}>
          <Stack gap="md">
            {error && <Callout tone="bad" live>{error}</Callout>}
            <TextInput
              label="Email address"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              error={!!error || undefined}
            />
            <PasswordInput
              label="Password"
              required
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              autoComplete="current-password"
              error={!!error || undefined}
            />
            <Checkbox label="Keep me signed in on this phone" checked={rememberMe} onChange={(e) => setRememberMe(e.currentTarget.checked)} />
            <Button type="submit" loading={submitting} leftSection={<IconLogin size={20} aria-hidden="true" />} fullWidth>Sign in</Button>
            <Anchor href="https://ccms.actschurch.co.za/auth/forgot-password" target="_blank" rel="noopener noreferrer" ta="center" style={{ display: 'block', padding: '12px 0' }}>
              Forgot your password?
            </Anchor>
          </Stack>
        </form>
      </div>
    </div>
  );
}
